import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useClerk, useUser } from "@clerk/clerk-react";
import {
  ArrowDownAZ,
  ChevronLeft,
  ChevronRight,
  Copy,
  Disc3,
  Eye,
  EyeOff,
  Globe,
  Heart,
  LayoutGrid,
  Library,
  ListMusic,
  ListOrdered,
  Lightbulb,
  LogOut,
  Mic2,
  Network,
  Pencil,
  Settings,
  Tags,
  Trash2,
} from "lucide-react";
import type { Playlist } from "#shared/schemas/playlist.ts";
import { cn } from "@web/lib/utils.ts";
import { Button } from "@web/components/ui/button.tsx";
import { PlaylistThumb } from "@web/components/PlaylistThumb.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@web/components/ui/context-menu.tsx";
import { ConfirmDialog, InlineRenameInput } from "@web/components/ConfirmDialog.tsx";
import {
  useAppStatus,
  useClonePlaylist,
  useDeleteSavedView,
  useHiddenPlaylists,
  useHidePlaylist,
  usePlaylists,
  useRenameSavedView,
  useSavedViews,
  useUnhidePlaylist,
  useConnectSpotify,
  useLogout,
} from "@web/features/library/api.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { SyncLibraryControl } from "./SyncLibraryControl.tsx";
import { WorkspaceSection } from "./WorkspaceSection.tsx";

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pt-4 pb-1 text-xs font-semibold tracking-wider text-faint uppercase">
      {children}
    </div>
  );
}

function sortSpotifyPlaylists(
  list: Playlist[],
  mode: "spotify" | "name",
): Playlist[] {
  if (mode === "name") {
    return [...list].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }
  // Keep the API order (library_position, else earliest track-added) unless we
  // have positions to sort by. Re-sorting nulls by name made both modes identical.
  const hasPositions = list.some((p) => p.library_position != null);
  if (!hasPositions) return list;
  return [...list].sort((a, b) => {
    const ap = a.library_position;
    const bp = b.library_position;
    if (ap != null && bp != null && ap !== bp) return ap - bp;
    if (ap != null && bp == null) return -1;
    if (ap == null && bp != null) return 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

function SideLink({
  to,
  icon,
  children,
  collapsed,
  end,
  onNavigate,
  /** Nav items sit in an unpadded scroller (`mx-2`). Footer already has `p-3`. */
  inset = true,
  iconClassName,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  collapsed: boolean;
  end?: boolean;
  onNavigate?: () => void;
  inset?: boolean;
  iconClassName?: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={typeof children === "string" ? children : undefined}
      onClick={() => onNavigate?.()}
      className={({ isActive }) =>
        cn(
          "flex items-center rounded-md px-3 text-muted transition-colors",
          "hover:bg-row-hover hover:text-text",
          inset
            ? "mx-2 gap-3 py-2 text-sm font-medium"
            : "h-7 w-full gap-2 text-xs font-semibold",
          collapsed && "justify-center px-0",
          isActive && "bg-row-hover text-text [&_svg]:text-accent",
        )
      }
    >
      <span className={cn("shrink-0 [&_svg]:size-5", iconClassName)}>{icon}</span>
      {!collapsed && <span className="truncate">{children}</span>}
    </NavLink>
  );
}

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const spotifyPlaylistSort = useUiStore((s) => s.spotifyPlaylistSort);
  const setSpotifyPlaylistSort = useUiStore((s) => s.setSpotifyPlaylistSort);
  const { signOut } = useClerk();
  const { user: clerkUser } = useUser();
  const status = useAppStatus();
  const playlists = usePlaylists();
  const hidden = useHiddenPlaylists();
  const views = useSavedViews();
  const logout = useLogout();
  const connectSpotify = useConnectSpotify();
  const hidePlaylist = useHidePlaylist();
  const unhidePlaylist = useUnhidePlaylist();
  const clonePlaylist = useClonePlaylist();
  const renameView = useRenameSavedView();
  const deleteView = useDeleteSavedView();
  const openDetail = useOpenDetail();
  const navigate = useNavigate();
  const [editingView, setEditingView] = useState<{
    id: string;
    draft: string;
    original: string;
  } | null>(null);
  const [pendingDeleteView, setPendingDeleteView] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [hiddenOpen, setHiddenOpen] = useState(false);

  const sortedPlaylists = useMemo(
    () => sortSpotifyPlaylists(playlists.data ?? [], spotifyPlaylistSort),
    [playlists.data, spotifyPlaylistSort],
  );
  const sortedHidden = useMemo(
    () => sortSpotifyPlaylists(hidden.data ?? [], spotifyPlaylistSort),
    [hidden.data, spotifyPlaylistSort],
  );

  function commitViewRename() {
    if (!editingView) return;
    const next = editingView.draft.trim();
    if (!next || next === editingView.original) {
      setEditingView(null);
      return;
    }
    renameView.mutate({ id: editingView.id, name: next });
    setEditingView(null);
  }

  return (
    <aside
      className={cn(
        "flex h-full shrink-0 flex-col rounded-lg bg-sidebar transition-[width] duration-200",
        collapsed ? "w-sidebar-rail" : "w-sidebar",
      )}
    >
      <div
        className={cn(
          "flex h-14 items-center gap-2 px-3",
          collapsed ? "justify-center" : "justify-between",
        )}
      >
        {!collapsed && (
          <div className="min-w-0 pl-1">
            <div className="truncate text-lg font-semibold tracking-tight">CoyKoi</div>
            <div className="truncate text-xs text-faint">
              {status.data?.user?.display_name ?? "Library"}
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={toggleSidebar}
        >
          {collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
        </Button>
      </div>

      <nav className="flex-1 overflow-y-auto pb-3">
        {!collapsed && <SectionLabel>Library</SectionLabel>}
        <SideLink to="/liked" icon={<Heart />} collapsed={collapsed}>
          Liked Songs
        </SideLink>
        <SideLink to="/all" icon={<Library />} collapsed={collapsed} end>
          All tracks
        </SideLink>
        <SideLink to="/artists" icon={<Mic2 />} collapsed={collapsed}>
          Artists
        </SideLink>
        <SideLink to="/albums" icon={<Disc3 />} collapsed={collapsed}>
          Albums
        </SideLink>
        <SideLink to="/playlists" icon={<ListMusic />} collapsed={collapsed}>
          Playlists
        </SideLink>
        <SideLink to="/genres" icon={<Tags />} collapsed={collapsed}>
          Genres
        </SideLink>

        {!collapsed && (
          <>
            <WorkspaceSection />

            <SectionLabel>Explore</SectionLabel>
          </>
        )}
        <SideLink to="/globe" icon={<Globe />} collapsed={collapsed}>
          Globe
        </SideLink>
        <SideLink
          to="/artists"
          icon={<Network />}
          collapsed={collapsed}
          onNavigate={() => useUiStore.getState().setArtistsView("graph")}
        >
          Artists graph
        </SideLink>

        {!collapsed && <SectionLabel>Ideas</SectionLabel>}
        <SideLink to="/ideas" icon={<Lightbulb />} collapsed={collapsed} end>
          Ideas
        </SideLink>

        {!collapsed && (
          <>
            <SectionLabel>Views</SectionLabel>
            {views.data?.map((view) =>
              editingView?.id === view.id ? (
                <div
                  key={view.id}
                  className="mx-2 flex items-center gap-2 rounded-md bg-row-hover py-1 pr-2 pl-3"
                >
                  <LayoutGrid className="size-4 shrink-0 text-accent" />
                  <InlineRenameInput
                    value={editingView.draft}
                    onChange={(draft) => setEditingView({ ...editingView, draft })}
                    onCommit={commitViewRename}
                    onCancel={() => setEditingView(null)}
                  />
                </div>
              ) : (
                <ContextMenu key={view.id}>
                  <ContextMenuTrigger asChild>
                    <div>
                      <SideLink
                        to={`/view/${view.id}`}
                        icon={<LayoutGrid />}
                        collapsed={false}
                      >
                        {view.name}
                      </SideLink>
                    </div>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem
                      onSelect={() =>
                        setEditingView({
                          id: view.id,
                          draft: view.name,
                          original: view.name,
                        })
                      }
                    >
                      <Pencil className="size-3.5" /> Rename
                    </ContextMenuItem>
                    <ContextMenuSeparator />
                    <ContextMenuItem
                      variant="destructive"
                      onSelect={() =>
                        setPendingDeleteView({ id: view.id, name: view.name })
                      }
                    >
                      <Trash2 className="size-3.5" /> Delete
                    </ContextMenuItem>
                  </ContextMenuContent>
                </ContextMenu>
              ),
            )}
            {views.data?.length === 0 && (
              <p className="px-4 py-1 text-xs text-faint">No saved views yet</p>
            )}

            <div className="flex items-center justify-between gap-2 px-3 pt-4 pb-1">
              <span className="text-xs font-semibold tracking-wider text-faint uppercase">
                Spotify
              </span>
              <div
                role="group"
                aria-label="Playlist sort"
                className="flex rounded-sm border border-line p-0.5"
              >
                <button
                  type="button"
                  title="Spotify library order"
                  aria-pressed={spotifyPlaylistSort === "spotify"}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase",
                    spotifyPlaylistSort === "spotify"
                      ? "bg-row-selected text-text"
                      : "text-faint hover:text-muted",
                  )}
                  onClick={() => setSpotifyPlaylistSort("spotify")}
                >
                  <ListOrdered className="size-3" />
                  Spotify
                </button>
                <button
                  type="button"
                  title="Sort by name A–Z"
                  aria-pressed={spotifyPlaylistSort === "name"}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase",
                    spotifyPlaylistSort === "name"
                      ? "bg-row-selected text-text"
                      : "text-faint hover:text-muted",
                  )}
                  onClick={() => setSpotifyPlaylistSort("name")}
                >
                  <ArrowDownAZ className="size-3" />
                  A–Z
                </button>
              </div>
            </div>
            {playlists.isLoading && (
              <div className="space-y-2 px-4 py-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            )}
            {sortedPlaylists.map((pl) => (
              <ContextMenu key={pl.id}>
                <ContextMenuTrigger asChild>
                  <div>
                    <SideLink
                      to={`/playlist/${pl.id}`}
                      icon={<PlaylistThumb coverUrl={pl.cover_url} alt={pl.name} />}
                      collapsed={false}
                    >
                      {pl.name}
                    </SideLink>
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuItem
                    onSelect={() => openDetail({ type: "playlist", id: pl.id })}
                  >
                    Details
                  </ContextMenuItem>
                  <ContextMenuItem
                    onSelect={() =>
                      void navigate(`/playlist/${encodeURIComponent(pl.id)}/explore`)
                    }
                  >
                    <Network className="size-3.5" /> Explore in graph
                  </ContextMenuItem>
                  <ContextMenuItem
                    disabled={clonePlaylist.isPending}
                    onSelect={() => {
                      void clonePlaylist.mutateAsync({ id: pl.id }).then((created) => {
                        void navigate(`/local/${created.id}`);
                      });
                    }}
                  >
                    <Copy className="size-3.5" /> Clone to workspace
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem onSelect={() => hidePlaylist.mutate(pl.id)}>
                    <EyeOff className="size-3.5" /> Hide from sidebar
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            ))}

            {(hidden.data?.length ?? 0) > 0 && (
              <div className="mt-1">
                <button
                  type="button"
                  className="mx-2 flex w-[calc(100%-1rem)] items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium text-faint hover:bg-row-hover hover:text-muted"
                  onClick={() => setHiddenOpen((v) => !v)}
                  aria-expanded={hiddenOpen}
                >
                  <Eye className="size-3.5" />
                  Hidden ({hidden.data?.length ?? 0})
                  <ChevronRight
                    className={cn(
                      "ml-auto size-3.5 transition-transform",
                      hiddenOpen && "rotate-90",
                    )}
                  />
                </button>
                {hiddenOpen &&
                  sortedHidden.map((pl) => (
                    <ContextMenu key={pl.id}>
                      <ContextMenuTrigger asChild>
                        <div>
                          <SideLink
                            to={`/playlist/${pl.id}`}
                            icon={<PlaylistThumb coverUrl={pl.cover_url} alt={pl.name} />}
                            collapsed={false}
                          >
                            {pl.name}
                          </SideLink>
                        </div>
                      </ContextMenuTrigger>
                      <ContextMenuContent>
                        <ContextMenuItem onSelect={() => unhidePlaylist.mutate(pl.id)}>
                          <Eye className="size-3.5" /> Restore to sidebar
                        </ContextMenuItem>
                        <ContextMenuItem
                          disabled={clonePlaylist.isPending}
                          onSelect={() => {
                            void clonePlaylist.mutateAsync({ id: pl.id }).then((created) => {
                              void navigate(`/local/${created.id}`);
                            });
                          }}
                        >
                          <Copy className="size-3.5" /> Clone to workspace
                        </ContextMenuItem>
                      </ContextMenuContent>
                    </ContextMenu>
                  ))}
              </div>
            )}
          </>
        )}
      </nav>

      <div
        className={cn(
          "space-y-1 border-t border-line p-3",
          collapsed && "flex flex-col items-center",
        )}
      >
        <SideLink
          to="/settings"
          icon={<Settings />}
          collapsed={collapsed}
          inset={false}
          iconClassName="[&_svg]:size-3.5"
        >
          Settings
        </SideLink>
        {status.data?.connected ? (
          <>
            <SyncLibraryControl collapsed={collapsed} />
            <Button
              variant="ghost"
              size={collapsed ? "icon-sm" : "sm"}
              className={cn(!collapsed && "w-full justify-start text-muted")}
              disabled={logout.isPending}
              onClick={() => logout.mutate()}
              aria-label="Disconnect Spotify"
            >
              {collapsed ? "⎋" : logout.isPending ? "Disconnecting…" : "Disconnect Spotify"}
            </Button>
          </>
        ) : (
          <Button
            size={collapsed ? "icon-sm" : "sm"}
            className={cn(!collapsed && "w-full")}
            disabled={connectSpotify.isPending}
            onClick={() => connectSpotify.mutate()}
          >
            {collapsed ? "♪" : connectSpotify.isPending ? "Connecting…" : "Connect Spotify"}
          </Button>
        )}
        <Button
          variant="ghost"
          size={collapsed ? "icon-sm" : "sm"}
          className={cn(!collapsed && "w-full justify-start text-muted")}
          onClick={() => void signOut({ redirectUrl: "/sign-in" })}
          aria-label="Sign out"
          title={clerkUser?.primaryEmailAddress?.emailAddress ?? "Sign out"}
        >
          {collapsed ? (
            <LogOut className="size-4" />
          ) : (
            <>
              <LogOut className="size-3.5" />
              Sign out
            </>
          )}
        </Button>
      </div>

      <ConfirmDialog
        open={pendingDeleteView !== null}
        title={`Delete “${pendingDeleteView?.name ?? ""}”?`}
        description="The saved view is removed. Tracks are untouched."
        confirmLabel="Delete"
        onCancel={() => setPendingDeleteView(null)}
        onConfirm={() => {
          if (!pendingDeleteView) return;
          deleteView.mutate(pendingDeleteView.id);
          setPendingDeleteView(null);
        }}
      />
    </aside>
  );
}
